import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Bar } from '@components'
import {
  AxisLegend,
  IGenericObject,
  ITrendLine,
  RangeParam,
  TableKey,
  TableWithFields,
} from '@types'
import useRest from '../../hooks/useRest'
import { generateQuery, roundTo } from '@utils'
import { Card } from '@ss/meta-core-components'
import { DateTime } from 'luxon'
import { StatisticsIcon } from '../../assets/icons/statisticsIcon'
import { config } from '../../config'

/**
 * Componente per la generazione di uno specchietto dettagli e di un grafico a barre
 * @param detail {string} chiave su cui si basa il grafico e i dettagli
 * @param params {TableWithFields} parametri per la query per reperire i dati
 * @param dataCy {string} selector per cypress
 * @component
 */
export const SelectedStartUpDetails: React.FC<{
  detail: TableKey
  params: TableWithFields
  dataCy: string
}> = ({ detail, params, dataCy }) => {
  const { post: getSummary } = useRest('/summary_statistics')
  const { post: getDistribution, abortRequest } = useRest(
    '/distribution_matching',
    { controlled: true }
  )

  const [tableData, setTableData] = useState<IGenericObject<any>[]>([])
  const [chartData, setChartData] = useState<{
    data?: IGenericObject<string>[]
    legend?: AxisLegend
    pdf?: ITrendLine
    best_distribution?: any
  }>({})

  const [type, setType] = useState<string>()

  const [bin, setBin] = useState<number>()

  useEffect(() => {
    setTableData([])
    setChartData({})
    setType(undefined)

    getSummary({
      index: params.index,
      field: detail.value,
      query_data: generateQuery({ params: params.filters }),
    }).then((res) => {
      if (res.type === 'integer') {
        setChartData({
          legend: { x: `${detail.value}`, y: 'Frequenza' },
          data: res.integer.terms.buckets.map(
            (data: { key: string; doc_count: number }) => ({
              x: data.key,
              y: data.doc_count,
            })
          ),
        })
        const newTab = Object.entries(res.integer.percentiles.values).map(
          ([key, value]) => ({
            key: `${key} percentile`,
            value: roundTo(value as number, 2),
          })
        )
        newTab.push({
          key: 'min',
          value: roundTo(res.integer.extended_stats.min, 2),
        })
        newTab.push({
          key: 'max',
          value: roundTo(res.integer.extended_stats.max, 2),
        })
        newTab.push({
          key: 'avg',
          value: roundTo(res.integer.extended_stats.avg, 2),
        })

        setTableData(newTab)
      }
      if (res.type === 'categorical') {
        setType('categorical')
        setChartData({
          data: res.categorical.map(
            (data: { key: string; doc_count: number }) => ({
              x: data.key,
              y: data.doc_count,
            })
          ),
          legend: { x: `${detail.value}`, y: 'Frequenza' },
        })

        setTableData(
          res.categorical
            .slice(0, 5)
            .map((data: { key: string; doc_count: number }) => ({
              key: data.key,
              value: data.doc_count,
            }))
        )
      }
      if (res.type === 'numerical') {
        setType('numerical')
        const newTab = Object.entries(res.numerical.percentiles.values).map(
          ([key, value]) => ({
            key: `${key} percentile`,
            value: roundTo(value as number, 2),
          })
        )
        newTab.push({
          key: 'min',
          value: roundTo(res.numerical.extended_stats.min, 2),
        })
        newTab.push({
          key: 'max',
          value: roundTo(res.numerical.extended_stats.max, 2),
        })
        newTab.push({
          key: 'avg',
          value: roundTo(res.numerical.extended_stats.avg, 2),
        })
        setTableData(newTab)

        if (detail.type === 'float') {
          setBin(200)

          const range: RangeParam = {
            field: detail.value,
            domain: [
              res.numerical.percentiles.values['5.0'] > 0
                ? res.numerical.percentiles.values['5.0']
                : 0.1,
              res.numerical.percentiles.values['95.0'],
            ],
          }

          getDistribution({
            index: params.index,
            fields: detail.value,
            modifier: 'log',
            bins: 200,
            query_data: generateQuery({
              params: params.filters,
              range: [range],
            }),
          }).then((res) => {
            setChartData(() => ({
              data: res.data,
              legend: { x: `${detail.value}`, y: res.labels.y },
              pdf: res.pdf,
              best_distribution: res.best_distribution,
            }))
          })
        }
      }
      if (res.type === 'date_ordinal') {
        setType('date_ordinal')
        setChartData({
          legend: { x: `${detail.value}`, y: 'Frequenza' },
          data: res.date_ordinal.map(
            (data: { key_as_string: string; doc_count: number }) => ({
              x: data.key_as_string,
              y: data.doc_count,
            })
          ),
        })
        setTableData(
          res.date_ordinal
            .slice(0, 5)
            .map((data: { key_as_string: string; doc_count: number }) => ({
              key: DateTime.fromISO(data.key_as_string).toFormat('dd/MM/yyyy'),
              value: data.doc_count,
            }))
        )
      }
    })

    return () => {
      abortRequest('POST', config.endpoint + '/distribution_matching')
    }
  }, [params, detail])

  return (
    <Container data-cy={`${dataCy}-container`}>
      <Card height={'100%'} width={'100%'}>
        <DescriptionBox data-cy={`${dataCy}-description-box`}>
          <TitleBox>
            <Icon>
              <StatisticsIcon />
            </Icon>
            <Label>DESCRIPTIVE STATS - {detail.value}</Label>
            {(type === 'categorical' || type === 'date_ordinal') && (
              <SubTitle>TOP 5 Frequencies</SubTitle>
            )}
          </TitleBox>
          <List>
            {tableData.map((el, i) => (
              <KeyValue key={i}>
                <Key>{el.key}</Key>
                <div>{el.value}</div>
              </KeyValue>
            ))}
          </List>
        </DescriptionBox>
      </Card>

      <ChartContainer data-cy={`${dataCy}-chart`}>
        <Bar
          bin={bin}
          PDF={chartData.pdf || undefined}
          axisLegend={chartData.legend}
          timeScale={
            type === 'date_ordinal'
              ? { format: '%Y-%m-%dT%H:%M', axis: 'x' }
              : undefined
          }
          data={chartData.data}
          indexBy={'x'}
          loading={chartData.data && chartData.data?.length >= 0}
          keys={['y']}
          barPadding={type === 'numerical' ? 0 : 0.2}
          margin={{ top: 15, right: 210, bottom: 40, left: 90 }}
          dimensions={{ height: type === 'numerical' ? '265px' : '100%' }}
        />
        {type === 'numerical' && (
          <DistributionMatchContainer>
            <div>
              <b>Best Distribution: </b>
              {chartData.best_distribution &&
                (chartData.best_distribution.name || '')}
            </div>
          </DistributionMatchContainer>
        )}
      </ChartContainer>
    </Container>
  )
}

//region STYLE
const DistributionMatchContainer = styled.div`
  width: 20%;
  text-align: center;
  padding: 10px;
`

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 25px;
  margin-bottom: 20px;
`

const DescriptionBox = styled.div`
  display: grid;
  max-height: 300px;
  height: 100%;
  grid-template-rows: 1fr 3fr;
`

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  height: 325px;
  border-radius: 5px;
  background-color: #1a3953;
`
const TitleBox = styled.div`
  display: grid;
  gap: 6px;
  grid-template-columns: 25px auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'icon title'
    'subtitle subtitle';
`
const Icon = styled.div`
  place-self: center;
  grid-area: icon;
`
const Label = styled.div`
  place-self: center start;
  font-weight: bold;
  font-size: 20px;
  color: var(--text);
  text-transform: uppercase;
  grid-area: title;
`
const SubTitle = styled.div`
  grid-area: subtitle;
`
const List = styled.div`
  display: grid;
  align-self: start;
  width: 100%;
  gap: 5px;
`
const KeyValue = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
`

const Key = styled.div`
  font-weight: bold;
`
//endregion
